import ArtistColorsProvider from "components/ArtistColorsProvider";
import PageBackground from "components/common/ArtistBackground";
import FailedSubscriptionBanner from "components/common/FailedSubscriptionBanner";
import { MetaCard } from "components/common/MetaCard";
import ReloadPrompt from "components/common/ReloadPrompt";
import Snackbar from "components/common/Snackbar";
import TranslationHelpBanner from "components/common/TranslationHelpBanner";
import UploadProgressPanel from "components/common/UploadProgressPanel";
import CookieDisclaimer from "components/CookieDisclaimer";
import { Footer } from "components/Footer";
import ManageArtistButtons from "components/ManageArtist/ManageArtistButtons";
import Player from "components/Player";
import useCurrentTrackHook from "components/Player/useCurrentTrackHook";
import ScrollToTop from "components/ScrollToTop";
import SetupBanner from "components/Setup/SetupBanner";
import useSetupRedirect from "components/Setup/useSetupRedirect";
import { isEmpty } from "lodash";
import { useInstanceSettings } from "queries/instanceSettings";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import SnackbarContext, { useSnackbar } from "state/SnackbarContext";
import { applyInstanceStyles } from "utils/instanceSettings";
import { useGlobalPlayerSyncIntegration } from "utils/playerSync";
import useRouteTitle from "utils/useRouteTitle";

import Header from "./components/Header/Header";

function App() {
  const { t } = useTranslation("translation", { keyPrefix: "app" });
  const { isDisplayed } = useContext(SnackbarContext);
  useGlobalPlayerSyncIntegration();
  const location = useLocation();
  const snackbar = useSnackbar();
  const [search, setSearch] = useSearchParams();
  const { currentTrack } = useCurrentTrackHook();
  const routeTitle = useRouteTitle();
  const instanceSettings = useInstanceSettings();
  const setupRedirect = useSetupRedirect();

  useEffect(() => {
    applyInstanceStyles(instanceSettings);
  }, [instanceSettings]);

  const isWidget = location.pathname.includes("widget");

  useEffect(() => {
    if (isWidget) {
      document.documentElement.dataset.widget = "true";
      return () => {
        delete document.documentElement.dataset.widget;
      };
    }
  }, [isWidget]);

  useEffect(() => {
    if (search.get("message")) {
      const message = search.get("message");
      if (message) {
        snackbar(message, { type: "warning" });
        setSearch((prev) => {
          prev.delete("message");
          return prev;
        });
      }
    }
  }, [search]);

  if (isWidget) {
    return <Outlet />;
  }

  if (setupRedirect) {
    return <Navigate to={setupRedirect} replace />;
  }

  const isPlayerVisible = !(!currentTrack || isEmpty(currentTrack.trackGroup));

  return (
    <>
      <ScrollToTop />
      <MetaCard
        title={routeTitle ?? instanceSettings.name}
        description="Hardcore punk underground online"
        image="/default-meta-image.webp"
      />
      <ArtistColorsProvider>
        <>
          {/* <Snackbar /> */}
          {isDisplayed && <Snackbar />}
          <div className="sr-only focus-within:not-sr-only flex gap-4 m-1!">
            <a href="#main-content">{t("skipToMainContent")}</a>
            {isPlayerVisible && <a href="#player">{t("skipToAudioPlayer")}</a>}
          </div>
          <Header />
          <ReloadPrompt />
          <FailedSubscriptionBanner />
          <SetupBanner />
          <TranslationHelpBanner />
          <CookieDisclaimer />
          <div className="hidden md:block">
            <PageBackground />
          </div>
          <div className="grow flex flex-col pb-[65px] min-h-[calc(100vh-65px)]">
            <ManageArtistButtons />
            <div className="w-full flex flex-col min-h-screen">
              <main
                className="mx-auto w-full rounded-[var(--mi-border-radius)] flex justify-center z-[1] grow"
                id="main-content"
              >
                <Outlet />
              </main>
              <Footer />
            </div>
          </div>
          <Player />
        </>
      </ArtistColorsProvider>
      <UploadProgressPanel />
    </>
  );
}

export default App;
